/**
 * [ABOUT]
 * Takes every middleware request, checks if it is an RSAA request.
 * If it is, it injects the "headers" object with "Authorization" header
 * that contains "bearer <token>". For the "types", it injects the <SUCCESS>
 * and <FAILURE> actions with a "meta" function, as specified by the RSAA
 * "redux-api-middleware" API. Injecting "meta" allows the <success> and
 * <failure> types to call that function, pull out the "Authorization" header
 * in the response headers, and updates the "window.session" and the
 * "window.sessionStorage" "ssSession" token, respectively.
 *
 * [NOTE]
 * Any requests that are not RSAA requests are forwarded to the next middleware.
 */

import moment from 'moment-timezone';
import { message } from 'antd';
import { RSAA } from 'redux-api-middleware';
import { Base64 } from 'js-base64';
import { ssSessionUtils, ssCommonUtils } from '@bit/ui-common.ui-components/ss-utils';

const
{ getDataType, setEnvPrefix } = ssCommonUtils,
{ htmlStorage, parseToken } = ssSessionUtils,
requestTimes = [],

rsaaJwtHandlerBeforeNote =
    'Injecting the "Authorization" header with the JWT token and inject ' +
    'a "meta" function into the <SUCCESS> and <FAILURE> types for updating ' +
    'the JWT token once returned.',

rsaaJwtHandlerAfterNote =
    'Reading the new JWT token from the Response header via the injected ' +
    '"meta" function. This will write the new data to window.session and ' +
    'parse the token into the HTML5 session storage.';

function rsaaJwtHanlderLog(logs, status, suffix, code) {
    if(process.env.NODE_ENV === 'development'.toUpperCase()) {
        console.groupCollapsed(
            `%c [${getDataType(status) === 'String' ? status : '-'}] ` +
            `jwtRsaaHanlder @ ${moment().format('YYYY-MM-DD HH:mm:ss')} ` +
            `${getDataType(suffix) === 'String' ? `-${code ? ` [${code}]` : ''} ${suffix}` : ''}`,
            'color: rgb(166, 166, 0);'
        );
        console.groupEnd();
    }
};

function RsaaJwtType(store, t) {
    let
    cb = null,
    c = {},
    access = {
        writable: true,
        enumerable: true,
        configurable: true
    },
    clearSession = () => {
        const { session } = window;
        var path = '/home';
        if (session && session.jwtSession && session.jwtSession.ROLES[0]) {
            switch ( session.jwtSession.ROLES[0] ) {
                case 'ADMIN':
                case 'ASI':
                    path = '/analyst';
                    break;
                case 'DOD':
                    path = '/home/dod';
                    break;
                case 'FSDO':
                    path = '/home/fsdo';
                    break;
                case 'PROVIDER_AGENT':
                case 'PROVIDER_ADMIN':
                    path = '/home/provider';
                    break;
            }
    
        }
        htmlStorage().clrAll();
        window.session = null;
        window.location.href = setEnvPrefix(path + '?session');
    },
    isExpired = ({ [RSAA]: { endpoint }}, state, { status }) => {
        let index = null, requestTime = null;
        for (let i in requestTimes)
            if (requestTimes[i].endpoint === endpoint) {
                index = parseInt(i);
                requestTime = {...requestTimes[i]};
                break;
            }

        if (index != null) {
            requestTimes.splice(index, 1);
            if(htmlStorage().get('ssSession').exp < requestTime.timestamp) return true;
        }

        switch(status) {
            case 406: clearSession();
            default: return false;
        }
    },
    meta = function(actions, state, res) {
        const { token } = htmlStorage().get('ssSession');
        let cbr = null, responseToken = res.headers.get('Authorization') || null;

        if (isExpired(actions, state, res)) clearSession();
        else if(responseToken != null) {
            if(token !== responseToken) {
                htmlStorage().set({ ssSession: parseToken(responseToken) });
                window.session = htmlStorage().get('ssSession');
            }
        }

        rsaaJwtHanlderLog([
            [ '%c NOTE     ', 'color: rgb(121, 121, 121);', rsaaJwtHandlerAfterNote ],
            [ '%c Session  ', 'color: rgb(0, 128, 192); font-weight: bold;', { 'window.session': window.session } ],
            [ '%c Response ', 'color: rgb(255, 128, 0); font-weight: bold;', res ],
            [ '%c State    ', 'color: rgb(197, 104, 0); font-weight: bold;', state ],
            [ '%c Actions  ', 'color: rgb(147, 64, 0); font-weight: bold;', actions ]
        ], 'AFTER RSAA', actions[RSAA].endpoint, res.status);

        if(cb != null) cbr = cb(actions, state, res);
        if(cbr != null && !cbr.hasOwnProperty('code')) cbr.code = res.code;

        return cbr;
    };

    switch(getDataType(t)) {
        case 'String':
            c.type = Object.assign({}, access, { value: t });
            c.meta = Object.assign({}, access, { value: meta });
            break;
        case 'Object':
            for(let p in t)
                if(p !== 'meta')
                    c[p] = Object.assign({}, access, { value: t[p] });

            if(t.hasOwnProperty('meta')) cb = t.meta;
            c.meta = Object.assign({}, access, { value: meta });
            break;
    }

    return Object.create(Object.prototype, c);
}

export default store => next => action => {
    const rsaa = action[RSAA] || null;

    try {
        if(rsaa != null) {
            let { sessionStorage } = window,
            modTypes = [ rsaa.types[0] ],
            token = sessionStorage.length > 0
                ? htmlStorage().get('ssSession').token
                : null;

            if(token != null && rsaa) {
                if(rsaa.headers == null) rsaa.headers = {};
                rsaa.headers = Object.assign({}, rsaa.headers, {
                    Authorization: `Bearer ${token}`
                });
            }

            for(let i=1; i < 3; i++)
                modTypes.push(RsaaJwtType(store, rsaa.types[i]));

            action = Object.create(Object.prototype, { [RSAA]: {
                value: {...rsaa, types: modTypes, credentials: 'same-origin' },
                writable: true,
                enumerable: true,
                configurable: true
            }});

            rsaaJwtHanlderLog([
                [ '%c NOTE    ', 'color: rgb(121, 121, 121);', rsaaJwtHandlerBeforeNote ],
                [ '%c Session ', 'color: rgb(0, 128, 192); font-weight: bold;', { 'window.session': window.session } ],
                [ '%c RSAA    ', 'color: rgb(255, 128, 0); font-weight: bold;', rsaa ],
                [ '%c Mods    ', 'color: rgb(187, 94, 0); font-weight: bold;', {
                    Headers: rsaa.headers,
                    Types: rsaa.types
                }],
            ], 'BEFORE RSAA', rsaa.endpoint);

            requestTimes.push({
                endpoint: rsaa.endpoint,
                timestamp: moment().unix()
            });
        }
    } catch(error) {
        rsaaJwtHanlderLog([[ '%c CATCH ', 'color: rgb(237, 28, 36);', error ]], 'BEFORE RSAA', rsaa.endpoint);
    }

    return next(action);
}
